import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Output,
    EventEmitter,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    effect,
    input,
    signal,
    computed,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25Util } from "../../util/s25-util";
import { DurationI } from "../../pojo/DurationI";
import { Proto } from "../../pojo/Proto";
import { AvailCheck } from "../../pojo/AvailabilityCheck";
import ISODurationString = Proto.ISODurationString;

@TypeManagerDecorator("layout-times")
@Component({
    selector: "layout-times",
    template: `
        @if (init()) {
            <div class="c-margin-bottom--double" role="alert" aria-live="assertive">
                <section class="cn-alert cn-alert--info cn-alert--notRealConflict">
                    <section class="cn-alert__icon cn-icon cn-alert__icon--blue" name="alert--info">
                        <strong>Warning:</strong> Unable to add <strong>{{ itemName() }}</strong
                        >: conflict due to location's setup and/or takedown times, which apply to all occurrences on
                        this segment. Consider adjusting the duration.
                    </section>
                </section>
            </div>

            @if (conflicts()?.length) {
                <div class="c-margin-bottom--double">
                    <s25-ng-conflicts [model]="conflicts()!"></s25-ng-conflicts>
                </div>
            }

            @if (!hasAnyEditableField()) {
                <p class="c-margin-bottom--double layout-times-readonly-message">
                    This location uses the default layout setup and takedown times shown below. You do not have
                    permission to change them.
                </p>
            } @else if (!canEditSetup()) {
                <p class="c-margin-bottom--double layout-times-readonly-message">
                    You do not have permission to change setup. Takedown may be adjusted below.
                </p>
            } @else if (!canEditTakedown()) {
                <p class="c-margin-bottom--double layout-times-readonly-message">
                    You do not have permission to change takedown. Setup may be adjusted below.
                </p>
            }

            <div class="layout-times-grid">
                <div class="grid-header"></div>
                <div class="grid-header">Days</div>
                <div class="grid-header">Hours</div>
                <div class="grid-header">Minutes</div>

                <div class="grid-label">Setup</div>
                @if (canEditSetup()) {
                    <s25-ng-editable-number
                        [val]="setupDays()"
                        [min]="0"
                        [max]="999"
                        [ariaLabel]="'Setup days'"
                        (valChange)="onSetupDaysChange($event)"
                    ></s25-ng-editable-number>
                    <s25-ng-editable-number
                        [val]="setupHours()"
                        [min]="0"
                        [max]="23"
                        [ariaLabel]="'Setup hours'"
                        (valChange)="onSetupHoursChange($event)"
                    ></s25-ng-editable-number>
                    <s25-ng-editable-number
                        [val]="setupMinutes()"
                        [min]="0"
                        [max]="59"
                        [ariaLabel]="'Setup minutes'"
                        (valChange)="onSetupMinutesChange($event)"
                    ></s25-ng-editable-number>
                } @else {
                    <div class="layout-times-readonly-cell">{{ setupDurationLabel() }}</div>
                }

                <div class="grid-label">Takedown</div>
                @if (canEditTakedown()) {
                    <s25-ng-editable-number
                        [val]="takedownDays()"
                        [min]="0"
                        [max]="999"
                        [ariaLabel]="'Takedown days'"
                        (valChange)="onTakedownDaysChange($event)"
                    ></s25-ng-editable-number>
                    <s25-ng-editable-number
                        [val]="takedownHours()"
                        [min]="0"
                        [max]="23"
                        [ariaLabel]="'Takedown hours'"
                        (valChange)="onTakedownHoursChange($event)"
                    ></s25-ng-editable-number>
                    <s25-ng-editable-number
                        [val]="takedownMinutes()"
                        [min]="0"
                        [max]="59"
                        [ariaLabel]="'Takedown minutes'"
                        (valChange)="onTakedownMinutesChange($event)"
                    ></s25-ng-editable-number>
                } @else {
                    <div class="layout-times-readonly-cell">{{ takedownDurationLabel() }}</div>
                }
            </div>

            <div class="button-container">
                @if (hasAnyEditableField()) {
                    <button class="aw-button aw-button--outline" (click)="onConfirm(false)">Cancel</button>
                    <button class="aw-button aw-button--primary" (click)="onConfirm(true)">Confirm</button>
                } @else {
                    <button class="aw-button aw-button--outline" (click)="onConfirm(false)">Close</button>
                }
            </div>
        }
    `,
    styles: [
        `
            .layout-times-grid {
                display: grid;
                grid-template-columns: auto repeat(3, 80px);
                gap: 0.5rem 1rem;
                align-items: center;
            }

            .grid-header {
                font-size: 0.75rem;
                text-transform: uppercase;
                font-weight: 500;
                text-align: center;
                color: #666;
            }

            .grid-label {
                font-weight: 600;
            }

            .layout-times-grid s25-ng-editable-number {
                text-align: center;
            }

            .layout-times-readonly-cell {
                grid-column: 2 / -1;
                align-self: center;
            }

            .button-container {
                display: flex;
                justify-content: flex-end;
                gap: 0.5rem;
                margin-top: 1.5rem;
            }
        `,
    ],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class LayoutTimesComponent {
    readonly setup = input<ISODurationString>("");
    readonly takedown = input<ISODurationString>("");
    readonly itemName = input<string>("");
    /** ANG-7691 conflict list when extending setup/takedown creates segment conflicts */
    readonly conflicts = input<AvailCheck.ConflictDefaults[] | undefined>(undefined);
    readonly canEditSetup = input<boolean>(true);
    readonly canEditTakedown = input<boolean>(true);

    @Output() valuesChanged = new EventEmitter<{
        takedown: string;
        setup: string;
        confirmed: boolean;
    }>();

    readonly init = signal(false);
    readonly setupDays = signal(0);
    readonly setupHours = signal(0);
    readonly setupMinutes = signal(0);
    readonly takedownDays = signal(0);
    readonly takedownHours = signal(0);
    readonly takedownMinutes = signal(0);

    readonly hasAnyEditableField = computed(() => this.canEditSetup() || this.canEditTakedown());

    readonly currentSetup = computed(() => {
        const durationObj: DurationI = {
            days: this.setupDays(),
            hours: this.setupHours(),
            minutes: this.setupMinutes(),
        };
        return S25Util.daysHoursMinutesToDuration(durationObj);
    });

    readonly currentTakedown = computed(() => {
        const durationObj: DurationI = {
            days: this.takedownDays(),
            hours: this.takedownHours(),
            minutes: this.takedownMinutes(),
        };
        return S25Util.daysHoursMinutesToDuration(durationObj);
    });

    readonly setupDurationLabel = computed(() => this.formatDurationLabel(this.setup()));
    readonly takedownDurationLabel = computed(() => this.formatDurationLabel(this.takedown()));

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this;

        effect(() => {
            const setupObj = S25Util.ISODurationToObj(this.setup() || "");
            this.setupDays.set(setupObj.days);
            this.setupHours.set(setupObj.hours);
            this.setupMinutes.set(setupObj.minutes);
        });

        effect(() => {
            const takedownObj = S25Util.ISODurationToObj(this.takedown() || "");
            this.takedownDays.set(takedownObj.days);
            this.takedownHours.set(takedownObj.hours);
            this.takedownMinutes.set(takedownObj.minutes);
            this.init.set(true);
        });
    }

    private formatDurationLabel(duration: ISODurationString): string {
        const parts = S25Util.ISODurationToObj(duration || "");
        const segments: string[] = [];
        if (parts.days) segments.push(`${parts.days} day${parts.days === 1 ? "" : "s"}`);
        if (parts.hours) segments.push(`${parts.hours} hour${parts.hours === 1 ? "" : "s"}`);
        if (parts.minutes) segments.push(`${parts.minutes} minute${parts.minutes === 1 ? "" : "s"}`);
        return segments.length ? segments.join(", ") : "0 minutes";
    }

    onSetupDaysChange(val: number): void {
        this.setupDays.set(val || 0);
    }

    onSetupHoursChange(val: number): void {
        this.setupHours.set(Math.min(val || 0, 23));
    }

    onSetupMinutesChange(val: number): void {
        this.setupMinutes.set(Math.min(val || 0, 59));
    }

    onTakedownDaysChange(val: number): void {
        this.takedownDays.set(val || 0);
    }

    onTakedownHoursChange(val: number): void {
        this.takedownHours.set(Math.min(val || 0, 23));
    }

    onTakedownMinutesChange(val: number): void {
        this.takedownMinutes.set(Math.min(val || 0, 59));
    }

    onConfirm(confirmed: boolean): void {
        this.valuesChanged.emit({
            setup: this.canEditSetup() ? this.currentSetup() : this.setup(),
            takedown: this.canEditTakedown() ? this.currentTakedown() : this.takedown(),
            confirmed,
        });
    }
}
